import { Card, Descriptions, Tag, Button, Space } from 'antd';
import {
  LogoutOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import { useAppNotifications } from '../../utils/notify';
import PageHeader from '../../components/PageHeader';
import LoadingBlock from '../../components/LoadingBlock';
import { formatDateTime } from '../../utils/format';

// Stage 5N-2C: read-only internal-admin profile + sign-out surface.
// Pulls everything from the auth context — /me runs on every page
// load via the InternalAdminAuthProvider boot/refresh cycle.

export default function AdminProfilePage() {
  const { admin, status, refreshMe, logout } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();
  const navigate = useNavigate();

  if (status === 'unknown' || !admin) {
    return <LoadingBlock tip="Loading profile…" />;
  }

  async function onRefresh() {
    try {
      await refreshMe();
      notifySuccess('Profile refreshed');
    } catch (err) {
      notifyError(err, { title: 'Could not refresh profile' });
    }
  }

  async function onLogout() {
    try {
      await logout();
      notifySuccess('Signed out');
    } catch (err) {
      notifyError(err, { title: 'Could not sign out cleanly' });
    } finally {
      navigate('/internal/login', { replace: true });
    }
  }

  return (
    <div>
      <PageHeader
        title="Admin Profile"
        subtitle="Your Glass-i internal admin identity, role, and permissions."
        actions={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={onRefresh}>
              Refresh
            </Button>
            <Button danger icon={<LogoutOutlined />} onClick={onLogout}>
              Sign out
            </Button>
          </Space>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card size="small" className="md:col-span-2">
          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-surface-200">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 text-2xl">
              <UserOutlined />
            </span>
            <div className="min-w-0 flex-1">
              <p className="m-0 text-lg font-bold text-ink-900 truncate">
                {admin.displayName || admin.email}
              </p>
              <p className="m-0 text-sm text-ink-500 truncate">{admin.email}</p>
            </div>
            <Tag color={admin.role === 'super_admin' ? 'red' : 'blue'} className="!text-sm">
              {admin.role}
            </Tag>
          </div>

          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="Display name">
              {admin.displayName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <code className="text-xs">{admin.email}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              <Tag color={admin.role === 'super_admin' ? 'red' : 'blue'}>{admin.role}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={admin.status === 'active' ? 'green' : 'orange'}>
                {admin.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="MFA required">
              {admin.mfaRequired ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag>No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Last login">
              {admin.lastLoginAt ? formatDateTime(admin.lastLoginAt) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {admin.createdAt ? formatDateTime(admin.createdAt) : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={
            <span className="flex items-center gap-2">
              <SafetyCertificateOutlined className="text-brand-500" />
              Permissions
            </span>
          }
        >
          {Array.isArray(admin.permissions) && admin.permissions.length > 0 ? (
            <ul className="list-none m-0 p-0 space-y-1.5">
              {admin.permissions.map((p) => (
                <li
                  key={p}
                  className="flex items-center gap-2 text-sm text-ink-800"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <code className="text-xs">{p}</code>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500 m-0">No permissions assigned.</p>
          )}
          <p className="text-xs text-ink-500 mt-3 mb-0 pt-3 border-t border-surface-200">
            Permissions are determined by your role. Contact a Glass-i super
            admin to change them.
          </p>
        </Card>
      </div>
    </div>
  );
}
